import React from "react"

import Flex from "@irati/ui/src/Flex"
import {Body, Label, H4, H6} from "@irati/ui/src/Typography"
import {FormattedCurrency, FormattedMessage} from "@irati/utils/lang"
import {UserRedemptionReportSavingsDataBreakdown, UserRedemptionReportSavingsDataBreakdownType} from "@irati/types"
import authentication from "@irati/auth"

import {DiscountIcon, FlexiPointsIcon, FlexiRewardsIcon, GiftCardsIcon} from "./MySavingsCard/Icons"

interface Props {
  breakdown?: UserRedemptionReportSavingsDataBreakdown[]
  showV2Details?: boolean
  breakdownTotalValue?: number
}

const BreakdownDetails = ({breakdown = [], showV2Details, breakdownTotalValue}: Props) => {
  if (showV2Details)
    return (
      <Flex direction="column">
        {breakdown.map((b) => {
          if (!authentication.apps("wallet.treatTokens") && b.type === UserRedemptionReportSavingsDataBreakdownType.FlexiPerks) {
            return null
          } else if (!authentication.apps("wallet.flexiRewards") && b.type === UserRedemptionReportSavingsDataBreakdownType.FlexiRewards) {
            return null
          } else if (breakdownTotalValue === 0) {
            return b.type === UserRedemptionReportSavingsDataBreakdownType.FlexiRewards ? null : (
              <Flex alignItems="center">
                {getTypeIcon(b.type)}
                <H6 as="span" marginLeft={1} marginRight={0.5}>
                  <FormattedMessage id="platform.mySavings.zeroState.breakdown.startSaving" />
                </H6>
                <Label>{getBreakdownLabel(b.type)}</Label>
              </Flex>
            )
          } else {
            return (
              <Flex alignItems="center">
                {getTypeIcon(b.type)}
                <H6 as="span" marginLeft={1} marginRight={0.5}>
                  <FormattedCurrency value={b.value} currency={b.currency} maximumFractionDigits={b.value ? 2 : 0} />
                </H6>
                <Label>{getBreakdownLabel(b.type)}</Label>
              </Flex>
            )
          }
        })}
      </Flex>
    )

  return (
    <>
      {breakdown.map((b, i) => (
        <Flex alignItems="center" marginTop={!i && 2} key={`estimatedBreakdown${i}`} gap={1}>
          {getTypeIcon(b.type)}
          <H4 as="span">
            <FormattedCurrency value={b.value} currency={b.currency} />
          </H4>
          <Body small>{getBreakdownLabel(b.type)}</Body>
        </Flex>
      ))}
    </>
  )
}

export default BreakdownDetails

const getBreakdownLabel = (breakdownType: UserRedemptionReportSavingsDataBreakdownType) => {
  switch (breakdownType) {
    case UserRedemptionReportSavingsDataBreakdownType.FlexiPerks:
      return <FormattedMessage id="platform.mySavings.breakdown.tooltip.flexiPerks" />
    case UserRedemptionReportSavingsDataBreakdownType.FlexiRewards:
      return <FormattedMessage id="platform.mySavings.breakdown.tooltip.flexiRewards" />
    case UserRedemptionReportSavingsDataBreakdownType.GiftCards:
      return <FormattedMessage id="platform.mySavings.breakdown.tooltip.giftCards" />
    case UserRedemptionReportSavingsDataBreakdownType.DiscountCodes:
      return <FormattedMessage id="platform.mySavings.breakdown.tooltip.discountCodes" />
    default:
      return null
  }
}
export const getTypeIcon = (breakdownType: string) => {
  switch (breakdownType) {
    case UserRedemptionReportSavingsDataBreakdownType.FlexiPerks:
    case "flexi_perk":
      return <FlexiPointsIcon />
    case UserRedemptionReportSavingsDataBreakdownType.FlexiRewards:
    case "reward":
      return <FlexiRewardsIcon />
    case UserRedemptionReportSavingsDataBreakdownType.DiscountCodes:
    case "% discount voucher":
      return <DiscountIcon />
    default:
      return <GiftCardsIcon />
  }
}
export const getTypeLabel = (breakdownType: UserRedemptionReportSavingsDataBreakdownType) => {
  switch (breakdownType) {
    case UserRedemptionReportSavingsDataBreakdownType.FlexiPerks:
      return <FormattedMessage id="platform.mySavings.type.flexiPerks.label" />
    case UserRedemptionReportSavingsDataBreakdownType.FlexiRewards:
      return <FormattedMessage id="platform.mySavings.type.flexiRewards.label" />
    case UserRedemptionReportSavingsDataBreakdownType.GiftCards:
      return <FormattedMessage id="platform.mySavings.type.giftCards.label" />
    case UserRedemptionReportSavingsDataBreakdownType.DiscountCodes:
      return <FormattedMessage id="platform.mySavings.type.discountCodes.label" />
    default:
      return null
  }
}
